import {clsx} from 'clsx';
import React, {useSyncExternalStore} from 'react';

import {type AnnotationHoverSource} from '../vis/annotations';
import {type IssueAlertModel} from './issueMarkers';

export type IssueAlertProps = {
  alert: IssueAlertModel;
  hoveredIssueSource: AnnotationHoverSource;
  onIssueHover?: (issueId: string | null) => void;
  onIssueSelect?: (issueId: string) => void;
};

/**
 * One issue marker or arrow. It subscribes to the hovered issue itself, so a
 * hover change re-renders only the markers whose highlight flips.
 */
export const IssueAlert = ({
  alert,
  hoveredIssueSource,
  onIssueHover,
  onIssueSelect,
}: IssueAlertProps) => {
  const {highlightable, id, index, label, left, right} = alert;
  const isActive = useSyncExternalStore(
    hoveredIssueSource.subscribe,
    function isHighlighted() {
      return highlightable && hoveredIssueSource.get() === id;
    }
  );

  const classes = clsx({
    'text-red-350': !isActive,
    'text-white': isActive,
    'bg-red-500': isActive,
    'bg-wb-surface': !isActive,
    'border-red-350': !isActive,
    'border-red-500': isActive,
  });

  const style: React.CSSProperties = {
    bottom: 0,
    zIndex: index,
  };
  if (typeof left === 'number') {
    style.left = left;
  }
  if (typeof right === 'number') {
    style.right = right;
  }

  function selectIssue() {
    onIssueSelect?.(id);
  }
  function hoverIssue() {
    onIssueHover?.(id);
  }
  function unhoverIssue() {
    onIssueHover?.(null);
  }

  return (
    <button
      style={style}
      key={`issue-alert-${index}`}
      className={`${classes} absolute flex h-12 w-12 cursor-pointer items-center justify-center rounded-sm border-[1px] text-[8px] transition-all duration-150 hover:bg-red-500 hover:text-white focus-visible:outline-0`}
      onClick={selectIssue}
      onMouseEnter={hoverIssue}
      onMouseLeave={unhoverIssue}>
      {label}
    </button>
  );
};
